import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll } from '@datachain/ui-sdk/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	exhaustMap,
	map,
	switchMap,
	tap,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { GroupingTagRequester } from '../../requesters';
import { GroupingTagAdapter } from '../grouping-tag.adapter';
import {
	createGroupingTag,
	createGroupingTagFailure,
	createGroupingTagSuccess,
	deleteGroupingTag,
	deleteGroupingTagFailure,
	deleteGroupingTagSuccess,
	fetchProjectCountByGroupingTag,
	fetchProjectCountByGroupingTagFailure,
	fetchProjectCountByGroupingTagSuccess,
	fetchReferencedProjectsByGroupingTagId,
	fetchReferencedProjectsByGroupingTagIdFailure,
	fetchReferencedProjectsByGroupingTagIdSuccess,
	getGroupingTagPageFailure,
	getGroupingTagsPage,
	getGroupingTagsPageSuccess,
	updateGroupingTag,
	updateGroupingTagFailure,
	updateGroupingTagSuccess,
} from './grouping-tag.actions';

@Injectable()
export class GroupingTagEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	// ##########################################################
	// Business Effects
	// ##########################################################
	public getGroupingTags$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getGroupingTagsPage),
			switchMap((action) =>
				this.requester.getGroupingTagPage(
					action.size,
					action.page,
					action.search,
					action.filteredFields,
					action.sortedFields,
					false,
					true
				)
			),
			map((response) =>
				getGroupingTagsPageSuccess({
					groupingTags: this.groupingTagAdapter.generateGroupingTagList(
						response.elements
					),
					total: response.total,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					getGroupingTagPageFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public createGroupingTag$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createGroupingTag),
			exhaustMap((action) =>
				this.requester.createGroupingTag(action.label, action.color)
			),
			map(() => createGroupingTagSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:@@groupingTag.create.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					createGroupingTagFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateGroupingTag$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateGroupingTag),
			exhaustMap((action) =>
				this.requester.updateGroupingTag(action.id, action.label, action.color)
			),
			map(() => updateGroupingTagSuccess()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateGroupingTagFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public projectsForGroupingTag$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fetchReferencedProjectsByGroupingTagId),
			exhaustMap((action) =>
				combineAll({
					groupingTag: action.id,
					projects: this.requester.getReferencedProjectsByGroupingTagId(
						action.id
					),
				})
			),
			map(({ groupingTag, projects }) =>
				this.groupingTagAdapter.toReferencingProject(groupingTag, projects)
			),
			map((res) => fetchReferencedProjectsByGroupingTagIdSuccess(res)),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchReferencedProjectsByGroupingTagIdFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public projectCountByGroupingTag$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fetchProjectCountByGroupingTag),
			switchMap((action) =>
				this.requester.getProjectsCountByGroupingTags(action.ids)
			),
			map((res) =>
				fetchProjectCountByGroupingTagSuccess({
					targets: res.map((r) => ({
						id: r.id,
						count: r.count,
					})),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchProjectCountByGroupingTagFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteGroupingTag$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteGroupingTag),
			switchMap((action) => {
				const toDelete = action.selected.map((s) =>
					this.requester.deleteGroupingTag(s)
				);
				return combineLatest(toDelete);
			}),
			map(() => deleteGroupingTagSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:@@groupingTag.delete.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					deleteGroupingTagFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public constructor(
		private readonly actions$: Actions,
		private readonly store: Store,
		private readonly requester: GroupingTagRequester,
		private readonly groupingTagAdapter: GroupingTagAdapter,
		private readonly ngCommService: NgCommService
	) {}
}
